import type { GameStateManager } from '../game/GameState';
import type { GameData, ActiveRealityEffect, RealityModifierEffect } from '../game/types';

export class EffectsDisplay {
  constructor(
    private container: HTMLElement,
    private gameState: GameStateManager,
    private gameData: GameData
  ) {}

  update(): void {
    const activeEffects = this.gameState.getActiveRealityEffects();
    this.render(activeEffects);
  }

  private getEffectName(effect: RealityModifierEffect): string {
    if (effect.recruitmentForbidden) return 'Recruitment Forbidden';
    if (effect.movementTimeMultiplier) return 'Temporal Distortion';
    if (effect.extraBudjetLossPerTick) return 'Phantom Researcher';
    return 'Unknown Effect';
  }

  private getEffectDescription(effect: RealityModifierEffect): string {
    if (effect.recruitmentForbidden) {
      return 'Recruitment actions are forbidden. No new researchers can join the facility.';
    }
    if (effect.movementTimeMultiplier) {
      return `Movement time increased by ${effect.movementTimeMultiplier}x. Time flows differently in the facility.`;
    }
    if (effect.extraBudjetLossPerTick) {
      return `Budget drain increased by ${effect.extraBudjetLossPerTick}/tick. A phantom presence drains resources.`;
    }
    return 'An unknown reality distortion affects the facility.';
  }

  private render(activeEffects: ActiveRealityEffect[]): void {
    if (activeEffects.length === 0) {
      this.container.innerHTML = '';
      return;
    }

    const badges = activeEffects.map(activeEffect => {
      const effectName = this.getEffectName(activeEffect.effect);
      const effectDescription = this.getEffectDescription(activeEffect.effect);
      
      // Map effect names to tooltip keys
      let tooltipKey = '';
      if (effectName === 'Recruitment Forbidden') {
        tooltipKey = 'reality_effect.recruitment_forbidden';
      } else if (effectName === 'Temporal Distortion') {
        tooltipKey = 'reality_effect.temporal_distortion';
      } else if (effectName === 'Phantom Researcher') {
        tooltipKey = 'reality_effect.phantom_researcher';
      }
      
      return `
        <div class="reality-effect-badge" 
             data-tooltip-key="${tooltipKey}"
             data-effect-name="${effectName}">
          <div class="effect-icon">⚠️</div>
          <div class="effect-name">${effectName}</div>
        </div>
      `;
    }).join('');

    this.container.innerHTML = `
      <div class="reality-effects-container">
        <div class="effects-header">Active Reality Effects</div>
        <div class="effects-badges">
          ${badges}
        </div>
      </div>
    `;
  }
}
